import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  FlatList,
  Modal,
  TextInput,
  Alert,
  RefreshControl,
  Image,
  Dimensions,
  Platform,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import AutomationRulesScreen from './AutomationRulesScreen';
import AutomatedHiringWorkflowScreen from '../core/AutomatedHiringWorkflow';
const { width, height } = Dimensions.get('window');
// ── API ─────────────────────────────────────────────────────────────────────
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const api = async (path, method = 'GET', body = null) => {
  const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw { status: res.status, ...e }; }
  return res.json();
};
const VideoInterviewPipelineScreen = ({ navigation, route }) => {
  // Safe parameter extraction
  const routeParams = route?.params || {};
  const jobId = routeParams.jobId || `job_${Date.now()}`;
  const jobTitle = routeParams.jobTitle || 'Software Engineer Position';
  const selectedApplicants = routeParams.selectedApplicants || [];
  const [companyLogo, setCompanyLogo] = useState(routeParams.companyLogo || null);
  const [companyName, setCompanyName] = useState(routeParams.companyName || '');

  // State management
  const [activeTab, setActiveTab] = useState('scheduled');
  const [videoInterviews, setVideoInterviews] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({
    applicantId: '',
    applicantName: '',
    interviewType: 'technical',
    date: '',
    time: '',
    duration: 60,
    interviewers: [],
    meetingLink: '',
    agenda: '',
    notes: '',
  });

  // Interview statistics
  const interviewStats = useMemo(() => {
    const stats = {
      total: videoInterviews.length,
      scheduled: 0,
      inProgress: 0,
      completed: 0,
      cancelled: 0,
      pending: 0,
    };

    videoInterviews.forEach(interview => {
      stats[interview.status] = (stats[interview.status] || 0) + 1;
    });

    return stats;
  }, [videoInterviews]);
  // Load video interviews
  // Reads from /api/recruitment/interviews?job_id=X — the SAME application_interviews
  // table the candidate's VideoInterviewScreen reads from. Both sides see each
  // other's status changes, join times, recording URLs, feedback.
  const loadVideoInterviews = useCallback(async () => {
    try {
      setLoading(true);
      const companyId = parseInt(await AsyncStorage.getItem('company_id') || '0', 10);
      const numericJobId = parseInt(jobId, 10);
      if (!companyId) {
        setVideoInterviews([]);
        return;
      }

      // Use the canonical recruitment/interviews endpoint. Filter by job_id
      // only when we have a real numeric one (some entry points pass synthetic
      // ids like `job_${Date.now()}` — those should show all interviews for the company).
      const qs = new URLSearchParams({
        company_id: String(companyId),
        type: 'video',          // this screen is video-specific
        limit: '200',
      });
      if (numericJobId && !Number.isNaN(numericJobId)) qs.set('job_id', String(numericJobId));

      try {
        const data = await api(`/api/recruitment/interviews?${qs.toString()}`);
        const rows = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
        // Normalize DB shape → the UI's expected fields
        const normalized = rows.map(row => ({
          id: String(row.id),
          applicationId: row.application_id,
          applicantId:   row.application_id,
          applicantName: row.candidate_name || 'Applicant',
          applicantEmail: row.candidate_email || '',
          applicantAvatar: row.candidate_avatar || null,
          position: row.job_title || '',
          jobId: row.job_id,
          interviewType: row.interview_type || 'video',
          date: row.scheduled_date ? new Date(row.scheduled_date).toISOString().slice(0, 10) : '',
          time: row.scheduled_date ? new Date(row.scheduled_date).toISOString().slice(11, 16) : '',
          scheduledTime: row.scheduled_date,
          duration: row.duration_minutes || 60,
          meetingLink: row.meeting_link || '',
          interviewers: Array.isArray(row.interviewers) ? row.interviewers : [],
          agenda: row.instructions || '',
          notes: row.interviewer_notes || row.feedback || '',
          status: row.status || 'scheduled',
          rating: Number(row.rating || 0),
          recordingUrl: row.recording_url || null,
          recordingEnabled: !!row.recording_enabled,
          candidateJoinedAt: row.candidate_joined_at,
          candidateLeftAt: row.candidate_left_at,
          decision: row.decision,
          stage: row.application_stage,
        }));
        setVideoInterviews(normalized);
        await AsyncStorage.setItem(`video_interviews_${jobId}`, JSON.stringify(normalized)).catch(() => {});
        return;
      } catch (apiErr) {
        console.warn('loadVideoInterviews API:', apiErr?.message);
      }

      // Offline cache fallback only — no mock data
      const cached = await AsyncStorage.getItem(`video_interviews_${jobId}`);
      if (cached) {
        try { setVideoInterviews(JSON.parse(cached)); } catch { setVideoInterviews([]); }
      } else {
        setVideoInterviews([]);
      }
    } catch (error) {
      console.warn('loadVideoInterviews:', error?.message);
      setVideoInterviews([]);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  // Save video interviews
  const saveVideoInterviews = useCallback(async (interviews) => {
    try {
      const storageKey = `video_interviews_${jobId}`;
      await AsyncStorage.setItem(storageKey, JSON.stringify(interviews));
    } catch (error) {
      console.error('Error saving video interviews:', error);
    }
  }, [jobId]);

  // Filter interviews by status
  const filteredInterviews = useMemo(() => {
    return videoInterviews.filter(interview => {
      switch (activeTab) {
        case 'scheduled':
          return interview.status === 'scheduled';
        case 'inProgress':
          return interview.status === 'inProgress';
        case 'completed':
          return interview.status === 'completed';
        case 'pending':
          return interview.status === 'pending';
        case 'cancelled':
          return interview.status === 'cancelled';
        default:
          return true;
      }
    });
  }, [videoInterviews, activeTab]);

  // Initialize component
  // Company logo — read cache first (instant), then refresh from API so it's
  // always up to date with whatever ProfileScreen / Settings has set.
  useFocusEffect(React.useCallback(() => { loadCompanyLogo(); }, []));
  const loadCompanyLogo = async () => {
    try {
      // 1. Hydrate from cache for an instant paint
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        setCompanyLogo(profile.logo || null);
        if (profile.name || profile.company_name) {
          setCompanyName(profile.name || profile.company_name);
        }
      }
      // 2. Re-fetch from the API so a logo change made elsewhere reflects here.
      const cid = await AsyncStorage.getItem('company_id');
      if (cid) {
        try {
          const r = await api(`/api/company-profile/${cid}`);
          const row = (r?.data && !Array.isArray(r.data)) ? r.data
                   : (Array.isArray(r?.data) ? r.data[0] : r);
          if (row && (row.logo || row.name || row.company_name)) {
            const logo = row.logo || null;
            const name = row.name || row.company_name || '';
            if (logo) setCompanyLogo(logo);
            if (name) setCompanyName(name);
            // Refresh cache so other screens benefit
            await AsyncStorage.setItem('company_profile', JSON.stringify(row));
            console.log('[VideoPipeline] company logo loaded from API:', !!logo);
          }
        } catch (e) {
          console.warn('[VideoPipeline] company-profile fetch:', e?.message);
        }
      }
    } catch (e) { console.warn('Logo load error:', e.message); }
  };

  useEffect(() => {
    loadVideoInterviews();
  }, [loadVideoInterviews]);

  // Refresh handler
  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadVideoInterviews();
    setRefreshing(false);
  }, [loadVideoInterviews]);

  // Schedule new interview
  const handleScheduleInterview = useCallback(async () => {
    try {
      if (!scheduleForm.applicantName || !scheduleForm.date || !scheduleForm.time) {
        Alert.alert('Error', 'Please fill in all required fields');
        return;
      }

      const companyId = parseInt(await AsyncStorage.getItem('company_id') || '0', 10) || null;
      // Combine the date + time fields into an ISO timestamp that the DB
      // column (scheduled_date timestamp) accepts. Local timezone is fine
      // since the same parsing is applied when reading back.
      const iso = new Date(`${scheduleForm.date}T${scheduleForm.time}:00`).toISOString();
      // If the recruiter pasted a meeting URL, use it. Otherwise generate a
      // real Jitsi Meet room (https://meet.jit.si accepts ANY room name and
      // works in browser + Jitsi app — no API key, no setup). The old default
      // was `https://meet.google.com/<random>` which Google rejects with
      // "meeting code not found", so candidates couldn't join.
      const meetingLink = scheduleForm.meetingLink
        || `https://meet.jit.si/jobeng-${jobId || 'interview'}-${Date.now()}`;

      // POST the interview row to the canonical table. Even if the form's
      // applicantId is a synthetic candidate id (not a numeric application_id),
      // we still write the row so the recruiter has a record; the candidate
      // side will join by candidate_email when the user logs in.
      const payload = {
        application_id:  parseInt(scheduleForm.applicantId, 10) || null,
        interview_type:  'video',
        scheduled_date:  iso,
        duration_minutes: parseInt(scheduleForm.duration, 10) || 60,
        meeting_link:    meetingLink,
        location:        '',
        status:          'scheduled',
        instructions:    scheduleForm.agenda || '',
        notes:           scheduleForm.notes  || '',
        round_number:    1,
        recording_enabled: true,
        interviewers:    scheduleForm.interviewers || [],
      };

      let dbId = null;
      try {
        const r = await api('/api/data/application_interviews', 'POST', payload);
        dbId = r?.id || r?.data?.id;
        console.log('[VideoPipeline] schedule POST ok id=', dbId);
      } catch (e) {
        console.warn('[VideoPipeline] schedule POST failed:', e?.message);
        Alert.alert(
          'Saved locally',
          'Could not reach the server. Interview saved to this device only.',
        );
      }

      // Mirror locally for instant UI feedback. `applicantPhoto` left null —
      // the candidate avatar is pulled from the joined applicant row when
      // the list reloads from the server.
      const newInterview = {
        id: dbId ? String(dbId) : `vi_${Date.now()}`,
        applicationId: payload.application_id,
        applicantId:   scheduleForm.applicantId || (dbId ? `app_${dbId}` : `cand_${Date.now()}`),
        applicantName: scheduleForm.applicantName,
        applicantPhoto: null,
        position:       jobTitle,
        interviewType:  scheduleForm.interviewType,
        status:         'scheduled',
        date:           scheduleForm.date,
        time:           scheduleForm.time,
        scheduledTime:  iso,
        duration:       payload.duration_minutes,
        meetingLink,
        interviewers:   payload.interviewers,
        agenda:         payload.instructions,
        notes:          payload.notes,
        score:          null,
        feedback:       '',
        recordingUrl:   '',
        recordingEnabled: true,
        createdAt:      new Date().toISOString(),
      };

      const updatedInterviews = [...videoInterviews, newInterview];
      setVideoInterviews(updatedInterviews);
      await saveVideoInterviews(updatedInterviews);

      setShowScheduleModal(false);
      setScheduleForm({
        applicantId: '',
        applicantName: '',
        interviewType: 'technical',
        date: '',
        time: '',
        duration: 60,
        interviewers: [],
        meetingLink: '',
        agenda: '',
        notes: '',
      });

      // After the local mirror, reload from server so the new row carries
      // any DB-side defaults (id, candidate join fields, etc.) the UI cares about.
      if (dbId) loadVideoInterviews();

      Alert.alert('Success', dbId
        ? 'Video interview scheduled and saved to the database.'
        : 'Video interview scheduled.',
      );
    } catch (error) {
      console.error('Error scheduling interview:', error);
      Alert.alert('Error', 'Failed to schedule interview');
    }
  }, [scheduleForm, videoInterviews, saveVideoInterviews, jobTitle, loadVideoInterviews]);

  // Start video interview
  const handleStartInterview = useCallback((interview) => {
    navigation.navigate('VideoScreen', {
      interviewId: interview.id,
      applicantName: interview.applicantName,
      interviewType: interview.interviewType,
      meetingLink: interview.meetingLink,
      agenda: interview.agenda,
      interviewers: interview.interviewers,
    });
  }, [navigation]);

  // Update interview status — also pushes the change to the DB so the
  // candidate side (VideoInterviewScreen) sees the new status immediately.
  // Mapped fields:
  //   newStatus      → status
  //   feedback       → feedback
  //   recordingUrl   → recording_url
  //   notes          → interviewer_notes (falls back to notes column)
  //   rating         → rating
  //   recordingEnabled → recording_enabled
  const updateInterviewStatus = useCallback(async (interviewId, newStatus, additionalData = {}) => {
    try {
      const updatedInterviews = videoInterviews.map(interview => {
        if (interview.id === interviewId) {
          return {
            ...interview,
            status: newStatus,
            ...additionalData,
          };
        }
        return interview;
      });

      setVideoInterviews(updatedInterviews);
      await saveVideoInterviews(updatedInterviews);

      // DB PUT — only when the local id is a real numeric DB id. The
      // schedule handler sets `vi_<timestamp>` if the POST failed; those
      // rows never made it to the server so a PUT would 404.
      if (/^\d+$/.test(String(interviewId))) {
        const payload = { status: newStatus };
        if (additionalData.feedback != null)        payload.feedback         = additionalData.feedback;
        if (additionalData.recordingUrl != null)    payload.recording_url    = additionalData.recordingUrl;
        if (additionalData.notes != null)           payload.notes            = additionalData.notes;
        if (additionalData.rating != null)          payload.rating           = additionalData.rating;
        if (additionalData.recordingEnabled != null)payload.recording_enabled = !!additionalData.recordingEnabled;
        if (additionalData.candidateJoinedAt)       payload.candidate_joined_at = additionalData.candidateJoinedAt;
        if (additionalData.candidateLeftAt)         payload.candidate_left_at   = additionalData.candidateLeftAt;
        if (additionalData.decision != null)        payload.decision         = additionalData.decision;
        try {
          await api(`/api/data/application_interviews/${interviewId}`, 'PUT', payload);
          console.log('[VideoPipeline] status PUT ok', interviewId, newStatus);
        } catch (e) {
          console.warn('[VideoPipeline] status PUT failed:', e?.message);
        }
      }
    } catch (error) {
      console.error('Error updating interview status:', error);
    }
  }, [videoInterviews, saveVideoInterviews]);

  // Render tab navigation
  const renderTabNavigation = () => {
    const tabs = [
      { key: 'scheduled', label: 'Scheduled', count: interviewStats.scheduled, color: '#3B82F6' },
      { key: 'inProgress', label: 'In Progress', count: interviewStats.inProgress, color: '#F59E0B' },
      { key: 'completed', label: 'Completed', count: interviewStats.completed, color: '#10B981' },
      { key: 'pending', label: 'Pending', count: interviewStats.pending, color: '#8B5CF6' },
      { key: 'cancelled', label: 'Cancelled', count: interviewStats.cancelled, color: '#EF4444' },
    ];

    return (
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.tabContainer}>
        {tabs.map((tab) => (
          <TouchableOpacity
            key={tab.key}
            style={[
              styles.tab,
              activeTab === tab.key && styles.activeTab,
              { borderBottomColor: tab.color }
            ]}
            onPress={() => setActiveTab(tab.key)}
          >
            <Text style={[
              styles.tabText,
              activeTab === tab.key && styles.activeTabText
            ]}>
              {tab.label}
            </Text>
            <View style={[styles.tabBadge, { backgroundColor: tab.color }]}>
              <Text style={styles.tabBadgeText}>{tab.count}</Text>
            </View>
          </TouchableOpacity>
        ))}
      </ScrollView>
    );
  };

  // Render interview item
  const renderInterviewItem = ({ item }) => {
    const getStatusColor = (status) => {
      switch (status) {
        case 'scheduled': return '#3B82F6';
        case 'inProgress': return '#F59E0B';
        case 'completed': return '#10B981';
        case 'pending': return '#8B5CF6';
        case 'cancelled': return '#EF4444';
        default: return '#6B7280';
      }
    };

    const getStatusIcon = (status) => {
      switch (status) {
        case 'scheduled': return 'calendar-clock';
        case 'inProgress': return 'video';
        case 'completed': return 'check-circle';
        case 'pending': return 'clock-outline';
        case 'cancelled': return 'cancel';
        default: return 'help-circle';
      }
    };

    return (
      <TouchableOpacity
        style={styles.interviewCard}
        onPress={() => {
          setSelectedInterview(item);
          setShowInterviewModal(true);
        }}
      >
        <View style={styles.interviewHeader}>
          <View style={styles.applicantInfo}>
            <Image source={{ uri: (item.applicantPhoto && typeof item.applicantPhoto==='string' && item.applicantPhoto.startsWith('http')) ? item.applicantPhoto : undefined }} style={styles.applicantPhoto} />
            <View style={styles.applicantDetails}>
              <Text style={styles.applicantName}>{item.applicantName}</Text>
              <Text style={styles.applicantPosition}>{item.position}</Text>
              <Text style={styles.interviewType}>{item.interviewType.charAt(0).toUpperCase() + item.interviewType.slice(1)} Interview</Text>
            </View>
          </View>
          <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) }]}>
            <MaterialCommunityIcons name={getStatusIcon(item.status)} size={16} color="#FFFFFF" />
            <Text style={styles.statusText}>{item.status.charAt(0).toUpperCase() + item.status.slice(1)}</Text>
          </View>
        </View>

        <View style={styles.interviewDetails}>
          <View style={styles.detailRow}>
            <MaterialCommunityIcons name="calendar" size={16} color="#6B7280" />
            <Text style={styles.detailText}>{item.date} at {item.time}</Text>
          </View>
          <View style={styles.detailRow}>
            <MaterialCommunityIcons name="clock-outline" size={16} color="#6B7280" />
            <Text style={styles.detailText}>{item.duration} minutes</Text>
          </View>
          <View style={styles.detailRow}>
            <MaterialCommunityIcons name="account-group" size={16} color="#6B7280" />
            <Text style={styles.detailText}>{item.interviewers.length} interviewer(s)</Text>
          </View>
        </View>

        {item.agenda && (
          <View style={styles.agendaSection}>
            <Text style={styles.agendaLabel}>Agenda:</Text>
            <Text style={styles.agendaText} numberOfLines={2}>{item.agenda}</Text>
          </View>
        )}

        {item.status === 'completed' && item.score && (
          <View style={styles.scoreSection}>
            <Text style={styles.scoreLabel}>Score:</Text>
            <View style={styles.scoreContainer}>
              <Text style={styles.scoreValue}>{item.score}/10</Text>
              <View style={styles.scoreBar}>
                <View style={[styles.scoreProgress, { width: `${(item.score / 10) * 100}%` }]} />
              </View>
            </View>
          </View>
        )}

        <View style={styles.interviewActions}>
          {item.status === 'scheduled' && (
            <>
              <TouchableOpacity
                style={[styles.actionButton, styles.startButton]}
                onPress={() => handleStartInterview(item)}
              >
                <MaterialCommunityIcons name="video" size={16} color="#FFFFFF" />
                <Text style={styles.actionButtonText}>Start Interview</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.actionButton, styles.rescheduleButton]}
                onPress={() => {
                  // Handle reschedule
                  Alert.alert('Reschedule', 'Reschedule functionality coming soon');
                }}
              >
                <MaterialCommunityIcons name="calendar-edit" size={16} color="#8B5CF6" />
                <Text style={[styles.actionButtonText, { color: '#8B5CF6' }]}>Reschedule</Text>
              </TouchableOpacity>
            </>
          )}
          {item.status === 'inProgress' && (
            <TouchableOpacity
              style={[styles.actionButton, styles.joinButton]}
              onPress={() => handleStartInterview(item)}
            >
              <MaterialCommunityIcons name="video" size={16} color="#FFFFFF" />
              <Text style={styles.actionButtonText}>Join Interview</Text>
            </TouchableOpacity>
          )}
          {item.status === 'completed' && (
            <>
              <TouchableOpacity
                style={[styles.actionButton, styles.viewButton]}
                onPress={() => {
                  // Handle view recording
                  Alert.alert('Recording', 'View recording functionality coming soon');
                }}
              >
                <MaterialCommunityIcons name="play-circle" size={16} color="#10B981" />
                <Text style={[styles.actionButtonText, { color: '#10B981' }]}>View Recording</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.actionButton, styles.feedbackButton]}
                onPress={() => {
                  // Handle feedback
                  Alert.alert('Feedback', 'Feedback functionality coming soon');
                }}
              >
                <MaterialCommunityIcons name="comment-text" size={16} color="#3B82F6" />
                <Text style={[styles.actionButtonText, { color: '#3B82F6' }]}>Feedback</Text>
              </TouchableOpacity>
            </>
          )}
        </View>
      </TouchableOpacity>
    );
  };

  // Render schedule modal
  const renderScheduleModal = () => (
    <Modal
      visible={showScheduleModal}
      transparent
      animationType="slide"
      onRequestClose={() => setShowScheduleModal(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Schedule Video Interview</Text>
            <TouchableOpacity onPress={() => setShowScheduleModal(false)}>
              <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.modalContent}>
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Applicant Name *</Text>
              <TextInput
                style={styles.formInput}
                value={scheduleForm.applicantName}
                onChangeText={(text) => setScheduleForm(prev => ({ ...prev, applicantName: text }))}
                placeholder="Enter applicant name"
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Interview Type</Text>
              <View style={styles.typeSelector}>
                {['technical', 'behavioral', 'portfolio', 'final'].map((type) => (
                  <TouchableOpacity
                    key={type}
                    style={[
                      styles.typeOption,
                      scheduleForm.interviewType === type && styles.selectedTypeOption
                    ]}
                    onPress={() => setScheduleForm(prev => ({ ...prev, interviewType: type }))}
                  >
                    <Text style={[
                      styles.typeOptionText,
                      scheduleForm.interviewType === type && styles.selectedTypeOptionText
                    ]}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View style={styles.formRow}>
              <View style={[styles.formGroup, { flex: 1, marginRight: 8 }]}>
                <Text style={styles.formLabel}>Date *</Text>
                <TextInput
                  style={styles.formInput}
                  value={scheduleForm.date}
                  onChangeText={(text) => setScheduleForm(prev => ({ ...prev, date: text }))}
                  placeholder="YYYY-MM-DD"
                />
              </View>
              <View style={[styles.formGroup, { flex: 1, marginLeft: 8 }]}>
                <Text style={styles.formLabel}>Time *</Text>
                <TextInput
                  style={styles.formInput}
                  value={scheduleForm.time}
                  onChangeText={(text) => setScheduleForm(prev => ({ ...prev, time: text }))}
                  placeholder="HH:MM AM/PM"
                />
              </View>
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Duration (minutes)</Text>
              <View style={styles.durationSelector}>
                {[30, 45, 60, 90, 120].map((duration) => (
                  <TouchableOpacity
                    key={duration}
                    style={[
                      styles.durationOption,
                      scheduleForm.duration === duration && styles.selectedDurationOption
                    ]}
                    onPress={() => setScheduleForm(prev => ({ ...prev, duration }))}
                  >
                    <Text style={[
                      styles.durationOptionText,
                      scheduleForm.duration === duration && styles.selectedDurationOptionText
                    ]}>
                      {duration}m
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Meeting Link</Text>
              <TextInput
                style={styles.formInput}
                value={scheduleForm.meetingLink}
                onChangeText={(text) => setScheduleForm(prev => ({ ...prev, meetingLink: text }))}
                placeholder="Auto-generated if empty"
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Agenda</Text>
              <TextInput
                style={[styles.formInput, styles.textArea]}
                value={scheduleForm.agenda}
                onChangeText={(text) => setScheduleForm(prev => ({ ...prev, agenda: text }))}
                placeholder="Interview agenda and topics to cover"
                multiline
                numberOfLines={3}
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Notes</Text>
              <TextInput
                style={[styles.formInput, styles.textArea]}
                value={scheduleForm.notes}
                onChangeText={(text) => setScheduleForm(prev => ({ ...prev, notes: text }))}
                placeholder="Additional notes about the applicant"
                multiline
                numberOfLines={3}
              />
            </View>
          </ScrollView>

          <View style={styles.modalActions}>
            <TouchableOpacity
              style={[styles.modalButton, styles.cancelButton]}
              onPress={() => setShowScheduleModal(false)}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.modalButton, styles.scheduleButton]}
              onPress={handleScheduleInterview}
            >
              <Text style={styles.scheduleButtonText}>Schedule Interview</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  // Render interview details modal
  const renderInterviewModal = () => (
    <Modal
      visible={showInterviewModal}
      transparent
      animationType="slide"
      onRequestClose={() => setShowInterviewModal(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Interview Details</Text>
            <TouchableOpacity onPress={() => setShowInterviewModal(false)}>
              <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
            </TouchableOpacity>
          </View>

          {selectedInterview && (
            <ScrollView style={styles.modalContent}>
              <View style={styles.interviewDetailSection}>
                <View style={styles.applicantHeader}>
                  <Image source={{ uri: (selectedInterview.applicantPhoto && selectedInterview.applicantPhoto.startsWith('http')) ? selectedInterview.applicantPhoto : undefined }} style={styles.applicantPhotoLarge} />
                  <View style={styles.applicantInfo}>
                    <Text style={styles.applicantNameLarge}>{selectedInterview.applicantName}</Text>
                    <Text style={styles.applicantPositionLarge}>{selectedInterview.position}</Text>
                    <Text style={styles.interviewTypeLarge}>{selectedInterview.interviewType.charAt(0).toUpperCase() + selectedInterview.interviewType.slice(1)} Interview</Text>
                  </View>
                </View>

                <View style={styles.detailGrid}>
                  <View style={styles.detailItem}>
                    <MaterialCommunityIcons name="calendar" size={20} color="#8B5CF6" />
                    <Text style={styles.detailLabel}>Date & Time</Text>
                    <Text style={styles.detailValue}>{selectedInterview.date} at {selectedInterview.time}</Text>
                  </View>
                  <View style={styles.detailItem}>
                    <MaterialCommunityIcons name="clock-outline" size={20} color="#8B5CF6" />
                    <Text style={styles.detailLabel}>Duration</Text>
                    <Text style={styles.detailValue}>{selectedInterview.duration} minutes</Text>
                  </View>
                  <View style={styles.detailItem}>
                    <MaterialCommunityIcons name="link" size={20} color="#8B5CF6" />
                    <Text style={styles.detailLabel}>Meeting Link</Text>
                    <Text style={styles.detailValue} numberOfLines={1}>{selectedInterview.meetingLink}</Text>
                  </View>
                </View>

                <View style={styles.interviewersSection}>
                  <Text style={styles.sectionTitle}>Interviewers</Text>
                  {selectedInterview.interviewers.map((interviewer) => (
                    <View key={interviewer.id} style={styles.interviewerItem}>
                      <Image source={{ uri: (interviewer.photo && interviewer.photo.startsWith('http')) ? interviewer.photo : undefined }} style={styles.interviewerPhoto} />
                      <View style={styles.interviewerInfo}>
                        <Text style={styles.interviewerName}>{interviewer.name}</Text>
                        <Text style={styles.interviewerRole}>{interviewer.role}</Text>
                      </View>
                    </View>
                  ))}
                </View>

                {selectedInterview.agenda && (
                  <View style={styles.agendaSection}>
                    <Text style={styles.sectionTitle}>Agenda</Text>
                    <Text style={styles.agendaTextLarge}>{selectedInterview.agenda}</Text>
                  </View>
                )}

                {selectedInterview.notes && (
                  <View style={styles.notesSection}>
                    <Text style={styles.sectionTitle}>Notes</Text>
                    <Text style={styles.notesText}>{selectedInterview.notes}</Text>
                  </View>
                )}

                {selectedInterview.status === 'completed' && (
                  <View style={styles.resultsSection}>
                    <Text style={styles.sectionTitle}>Interview Results</Text>
                    {selectedInterview.score && (
                      <View style={styles.scoreDisplay}>
                        <Text style={styles.scoreLabel}>Overall Score</Text>
                        <View style={styles.scoreContainer}>
                          <Text style={styles.scoreValueLarge}>{selectedInterview.score}/10</Text>
                          <View style={styles.scoreBarLarge}>
                            <View style={[styles.scoreProgressLarge, { width: `${(selectedInterview.score / 10) * 100}%` }]} />
                          </View>
                        </View>
                      </View>
                    )}
                    {selectedInterview.feedback && (
                      <View style={styles.feedbackSection}>
                        <Text style={styles.feedbackLabel}>Feedback</Text>
                        <Text style={styles.feedbackText}>{selectedInterview.feedback}</Text>
                      </View>
                    )}
                  </View>
                )}
              </View>
            </ScrollView>
          )}

          <View style={styles.modalActions}>
            {selectedInterview?.status === 'scheduled' && (
              <TouchableOpacity
                style={[styles.modalButton, styles.startInterviewButton]}
                onPress={() => {
                  setShowInterviewModal(false);
                  handleStartInterview(selectedInterview);
                }}
              >
                <MaterialCommunityIcons name="video" size={16} color="#FFFFFF" />
                <Text style={styles.startInterviewButtonText}>Start Interview</Text>
              </TouchableOpacity>
            )}
            {selectedInterview?.status === 'inProgress' && (
              <TouchableOpacity
                style={[styles.modalButton, styles.joinInterviewButton]}
                onPress={() => {
                  setShowInterviewModal(false);
                  handleStartInterview(selectedInterview);
                }}
              >
                <MaterialCommunityIcons name="video" size={16} color="#FFFFFF" />
                <Text style={styles.joinInterviewButtonText}>Join Interview</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <LinearGradient colors={['#8B5CF6', '#7C3AED']} style={styles.header}>
        <View style={styles.headerContent}>
          <TouchableOpacity onPress={() => navigation.goBack()}>
            <MaterialCommunityIcons name="arrow-left" size={24} color="#FFFFFF" />
          </TouchableOpacity>
          <View style={styles.headerTitleContainer}>
            <Text style={styles.headerTitle}>Video Interviews</Text>
            <Text style={styles.headerSubtitle}>{jobTitle}</Text>
          </View>
          <TouchableOpacity
            style={styles.scheduleButton}
            onPress={() => setShowScheduleModal(true)}
          >
            <MaterialCommunityIcons name="plus" size={24} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      </LinearGradient>

      {/* Statistics */}
      <View style={styles.statsContainer}>
        <View style={styles.statItem}>
          <Text style={styles.statValue}>{interviewStats.total}</Text>
          <Text style={styles.statLabel}>Total</Text>
        </View>
        <View style={styles.statItem}>
          <Text style={[styles.statValue, { color: '#3B82F6' }]}>{interviewStats.scheduled}</Text>
          <Text style={styles.statLabel}>Scheduled</Text>
        </View>
        <View style={styles.statItem}>
          <Text style={[styles.statValue, { color: '#F59E0B' }]}>{interviewStats.inProgress}</Text>
          <Text style={styles.statLabel}>In Progress</Text>
        </View>
        <View style={styles.statItem}>
          <Text style={[styles.statValue, { color: '#10B981' }]}>{interviewStats.completed}</Text>
          <Text style={styles.statLabel}>Completed</Text>
        </View>
      </View>

      {/* Tab Navigation */}
      {renderTabNavigation()}

      {/* Interview List */}
      <FlatList
        data={filteredInterviews}
        renderItem={renderInterviewItem}
        keyExtractor={(item) => item.id}
        contentContainerStyle={styles.listContainer}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <MaterialCommunityIcons name="video-off" size={64} color="#D1D5DB" />
            <Text style={styles.emptyTitle}>No {activeTab} interviews</Text>
            <Text style={styles.emptySubtitle}>
              {activeTab === 'scheduled' 
                ? 'Schedule your first video interview to get started'
                : `No interviews in ${activeTab} status`
              }
            </Text>
            {activeTab === 'scheduled' && (
              <TouchableOpacity
                style={styles.emptyButton}
                onPress={() => setShowScheduleModal(true)}
              >
                <Text style={styles.emptyButtonText}>Schedule Interview</Text>
              </TouchableOpacity>
            )}
          </View>
        }
      />

      {/* Modals */}
      {renderScheduleModal()}
      {renderInterviewModal()}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  header: {
    paddingTop: Platform.OS === 'ios' ? 0 : 20,
    paddingBottom: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
  },
  headerTitleContainer: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  headerSubtitle: {
    fontSize: 14,
    color: '#E5E7EB',
    marginTop: 2,
  },
  scheduleButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statsContainer: {
    flexDirection: 'row',
    backgroundColor: '#FFFFFF',
    paddingVertical: 20,
    paddingHorizontal: 20,
    marginHorizontal: 20,
    marginTop: -10,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  statItem: {
    flex: 1,
    alignItems: 'center',
  },
  statValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1F2937',
  },
  statLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 4,
  },
  tabContainer: {
    backgroundColor: '#FFFFFF',
    paddingVertical: 16,
    paddingHorizontal: 20,
    marginTop: 20,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginRight: 16,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  activeTab: {
    borderBottomWidth: 2,
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6B7280',
    marginRight: 8,
  },
  activeTabText: {
    color: '#1F2937',
    fontWeight: '600',
  },
  tabBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    minWidth: 20,
    alignItems: 'center',
  },
  tabBadgeText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  listContainer: {
    padding: 20,
  },
  interviewCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  interviewHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  applicantInfo: {
    flexDirection: 'row',
    flex: 1,
  },
  applicantPhoto: {
    width: 50,
    height: 50,
    borderRadius: 25,
    marginRight: 12,
  },
  applicantDetails: {
    flex: 1,
  },
  applicantName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
  },
  applicantPosition: {
    fontSize: 14,
    color: '#6B7280',
    marginTop: 2,
  },
  interviewType: {
    fontSize: 12,
    color: '#8B5CF6',
    marginTop: 2,
    fontWeight: '500',
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 4,
  },
  interviewDetails: {
    marginBottom: 12,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  detailText: {
    fontSize: 14,
    color: '#6B7280',
    marginLeft: 8,
  },
  agendaSection: {
    marginBottom: 12,
  },
  agendaLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 4,
  },
  agendaText: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
  },
  scoreSection: {
    marginBottom: 12,
  },
  scoreLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 8,
  },
  scoreContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  scoreValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#10B981',
    marginRight: 12,
  },
  scoreBar: {
    flex: 1,
    height: 6,
    backgroundColor: '#E5E7EB',
    borderRadius: 3,
  },
  scoreProgress: {
    height: '100%',
    backgroundColor: '#10B981',
    borderRadius: 3,
  },
  interviewActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    flex: 1,
    marginHorizontal: 4,
    justifyContent: 'center',
  },
  startButton: {
    backgroundColor: '#10B981',
  },
  joinButton: {
    backgroundColor: '#F59E0B',
  },
  rescheduleButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#8B5CF6',
  },
  viewButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#10B981',
  },
  feedbackButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#3B82F6',
  },
  actionButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 4,
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#374151',
    marginTop: 16,
  },
  emptySubtitle: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
    marginTop: 8,
    paddingHorizontal: 40,
  },
  emptyButton: {
    backgroundColor: '#8B5CF6',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: 20,
  },
  emptyButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    width: width * 0.9,
    maxHeight: height * 0.8,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1F2937',
  },
  modalContent: {
    padding: 20,
  },
  formGroup: {
    marginBottom: 16,
  },
  formLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 8,
  },
  formInput: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    color: '#1F2937',
  },
  textArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  formRow: {
    flexDirection: 'row',
  },
  typeSelector: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  typeOption: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    marginRight: 8,
    marginBottom: 8,
  },
  selectedTypeOption: {
    backgroundColor: '#8B5CF6',
    borderColor: '#8B5CF6',
  },
  typeOptionText: {
    fontSize: 14,
    color: '#6B7280',
  },
  selectedTypeOptionText: {
    color: '#FFFFFF',
  },
  durationSelector: {
    flexDirection: 'row',
  },
  durationOption: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    marginRight: 8,
  },
  selectedDurationOption: {
    backgroundColor: '#8B5CF6',
    borderColor: '#8B5CF6',
  },
  durationOptionText: {
    fontSize: 14,
    color: '#6B7280',
  },
  selectedDurationOptionText: {
    color: '#FFFFFF',
  },
  modalActions: {
    flexDirection: 'row',
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
  modalButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
    marginHorizontal: 8,
  },
  cancelButton: {
    backgroundColor: '#F3F4F6',
  },
  cancelButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6B7280',
  },
  scheduleButton: {
    backgroundColor: '#8B5CF6',
  },
  scheduleButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  startInterviewButton: {
    backgroundColor: '#10B981',
    flexDirection: 'row',
  },
  startInterviewButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 4,
  },
  joinInterviewButton: {
    backgroundColor: '#F59E0B',
    flexDirection: 'row',
  },
  joinInterviewButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 4,
  },
  interviewDetailSection: {
    marginBottom: 20,
  },
  applicantHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  applicantPhotoLarge: {
    width: 80,
    height: 80,
    borderRadius: 40,
    marginRight: 16,
  },
  applicantNameLarge: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1F2937',
  },
  applicantPositionLarge: {
    fontSize: 16,
    color: '#6B7280',
    marginTop: 4,
  },
  interviewTypeLarge: {
    fontSize: 14,
    color: '#8B5CF6',
    marginTop: 4,
    fontWeight: '600',
  },
  detailGrid: {
    marginBottom: 20,
  },
  detailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  detailLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    marginLeft: 12,
    flex: 1,
  },
  detailValue: {
    fontSize: 14,
    color: '#6B7280',
    flex: 2,
  },
  interviewersSection: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 12,
  },
  interviewerItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  interviewerPhoto: {
    width: 40,
    height: 40,
    borderRadius: 20,
    marginRight: 12,
  },
  interviewerInfo: {
    flex: 1,
  },
  interviewerName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  interviewerRole: {
    fontSize: 12,
    color: '#6B7280',
  },
  agendaTextLarge: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
  },
  notesSection: {
    marginBottom: 20,
  },
  notesText: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
  },
  resultsSection: {
    marginBottom: 20,
  },
  scoreDisplay: {
    marginBottom: 16,
  },
  scoreValueLarge: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#10B981',
    marginRight: 16,
  },
  scoreBarLarge: {
    flex: 1,
    height: 8,
    backgroundColor: '#E5E7EB',
    borderRadius: 4,
  },
  scoreProgressLarge: {
    height: '100%',
    backgroundColor: '#10B981',
    borderRadius: 4,
  },
  feedbackSection: {
    marginTop: 12,
  },
  feedbackLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 8,
  },
  feedbackText: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
  },
});

export default VideoInterviewPipelineScreen;